<div class="py-2 sm:py-6">
  <div class="px-4 sm:px-6 lg:px-8">
    <!-- Header -->
    <div class="text-center mb-5 sm:mb-6">
      <h2 class="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">{{ 'Dane rozliczeniowe i dostawy' | translate }}</h2>
      <p class="text-sm sm:text-base text-gray-500 mt-1 max-w-xl mx-auto">{{ 'Podaj dane do faktury oraz dostawy, aby kontynuować składanie zamówienia.' | translate }}</p>
    </div>

    <form [formGroup]="formGroup" class="space-y-4 sm:space-y-5">
      <!-- Auto-fill & Editable Notice Banner -->
      <div *ngIf="authService.isLoggedIn()" class="flex items-start sm:items-center gap-3 p-3.5 sm:p-4 bg-orange-50/70 border border-orange-200/80 rounded-xl shadow-xs">
        <div class="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center shrink-0 text-brocom-orange mt-0.5 sm:mt-0">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div class="flex-1 text-xs sm:text-sm">
          <span class="font-semibold text-gray-900">{{ 'Dane zostały uzupełnione na podstawie Twojego konta.' | translate }}</span>
          <span class="text-gray-600 block sm:inline sm:ml-1">{{ 'Przed złożeniem zamówienia możesz je sprawdzić i edytować.' | translate }}</span>
        </div>
      </div>

      <fieldset *ngIf="!authService.isLoggedIn()" class="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-gray-200">
        <legend class="text-sm sm:text-base font-bold text-gray-900 px-1">{{ 'Rodzaj nabywcy' | translate }}</legend>
        <p class="text-xs sm:text-sm text-gray-500 mb-3">{{ 'Wybierz, czy zamówienie składa osoba prywatna, czy firma.' | translate }}</p>
        <div class="flex flex-col sm:flex-row gap-3">
          <label class="flex items-center gap-2 p-3 border border-gray-200 rounded-lg cursor-pointer">
            <input type="radio" formControlName="accountType" value="individual" />
            <span class="text-sm text-gray-800">{{ 'Osoba prywatna' | translate }}</span>
          </label>
          <label class="flex items-center gap-2 p-3 border border-gray-200 rounded-lg cursor-pointer">
            <input type="radio" formControlName="accountType" value="company" />
            <span class="text-sm text-gray-800">{{ 'Firma' | translate }}</span>
          </label>
        </div>
      </fieldset>

      <div *ngIf="isCompanyUser" class="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-gray-200">
          <div class="pb-2.5 mb-3 border-b border-gray-100">
            <h3 class="text-base sm:text-lg font-bold text-gray-900">
              {{ 'Dane firmy' | translate }}
            </h3>
            <p class="text-xs text-gray-500 mt-0.5">{{ 'Podaj dane rejestrowe firmy.' | translate }}</p>
          </div>

          <!-- Company Information Validation Message -->
          <div *ngIf="hasCompanyInformationErrors()"
            class="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
            <div class="flex items-start">
              <svg class="w-4 h-4 sm:w-5 sm:h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" fill="none"
                stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div>
                <p class="text-xs sm:text-sm font-semibold text-red-800 mb-1">{{ 'Uzupełnij wymagane dane firmy.' | translate }}</p>
                <p class="text-xs sm:text-sm text-red-700">{{ 'Brakujące pola:' | translate }} {{ getMissingCompanyFields().join(',
                  ') }}</p>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
            <div>
              <label for="companyName" class="block text-xs sm:text-sm font-bold text-gray-900 mb-1 sm:mb-2">
                {{ 'Nazwa firmy' | translate }} <span class="text-orange-500">*</span>
              </label>
              <input id="companyName" formControlName="companyName" type="text"
                class="w-full px-3 sm:px-4 py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange focus:border-transparent transition-all"
                [placeholder]="'Wpisz nazwę firmy' | translate" required />
              <div *ngIf="formGroup.get('companyName')?.invalid && formGroup.get('companyName')?.touched"
                class="text-red-500 text-xs mt-1">
                {{ 'Nazwa firmy jest wymagana' | translate }}
              </div>
            </div>

            <div>
              <label for="industrysector" class="block text-sm font-semibold text-gray-700 mb-2">
                {{ 'Branża / dział (opcjonalnie)' | translate }}
              </label>
              <input id="industrysector" formControlName="industrysector" type="text"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange focus:border-transparent transition-all"
                [placeholder]="'Wpisz branżę lub dział' | translate" />
            </div>

            <div>
              <label for="vatNumber" class="block text-sm font-semibold text-gray-700 mb-2">
                {{ 'NIP' | translate }}
              </label>
              <input id="vatNumber" formControlName="vatNumber" type="text"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange focus:border-transparent transition-all"
                [placeholder]="'Wpisz NIP' | translate" />
            </div>
          </div>
        </div>

        <!-- Address Information -->
        <div class="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-gray-200">
          <div class="pb-2.5 mb-3 border-b border-gray-100">
            <h3 class="text-base sm:text-lg font-bold text-gray-900">
              {{ 'Dane adresowe' | translate }}
            </h3>
            <p class="text-xs text-gray-500 mt-0.5">{{ 'Podaj adres nabywcy, który zostanie umieszczony na fakturze.' | translate }}</p>
          </div>

          <!-- Address Information Validation Message -->
          <div *ngIf="hasAddressInformationErrors()"
            class="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div class="flex items-start">
              <svg class="w-4 h-4 sm:w-5 sm:h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" fill="none"
                stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div>
                <p class="text-xs sm:text-sm font-semibold text-red-800 mb-1">{{ 'Uzupełnij wymagane dane adresowe.' | translate }}</p>
                <p class="text-xs sm:text-sm text-red-700">{{ 'Brakujące pola:' | translate }} {{ getMissingAddressFields().join(',
                  ') }}</p>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
            <div>
              <label for="postcodeTown" class="block text-xs sm:text-sm font-bold text-gray-900 mb-1 sm:mb-2">
                {{ 'Ulica i numer' | translate }} <span class="text-orange-500">*</span>
              </label>
              <input id="postcodeTown" formControlName="postcodeTown" type="text"
                class="w-full px-3 sm:px-4 py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange focus:border-transparent transition-all"
                [placeholder]="'np. ul. Przykładowa 123' | translate" required />
              <div *ngIf="formGroup.get('postcodeTown')?.invalid && formGroup.get('postcodeTown')?.touched"
                class="text-red-500 text-xs mt-1">
                {{ 'Ulica i numer są wymagane' | translate }}
              </div>
            </div>

            <div>
              <label for="stateProvince" class="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                {{ 'Województwo / region' | translate }}
              </label>
              <input id="stateProvince" formControlName="stateProvince" type="text"
                class="w-full px-3 sm:px-4 py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange focus:border-transparent transition-all"
                [placeholder]="'Enter state, province or region' | translate" />
            </div>

            <div>
              <label for="postalCode" class="block text-xs sm:text-sm font-bold text-gray-900 mb-1 sm:mb-2">
                {{ 'Postal Code' | translate }} <span class="text-orange-500">*</span>
              </label>
              <div class="relative">
                <input id="postalCode" formControlName="postalCode" type="text"
                  class="w-full px-3 sm:px-4 py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange focus:border-transparent transition-all"
                  [class.border-red-500]="(formGroup.get('postalCode')?.invalid && formGroup.get('postalCode')?.touched) || postalCodeInvalid"
                  [placeholder]="'12345' | translate" required />
                <div *ngIf="isCheckingPostalCode" class="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
                  <svg class="animate-spin h-4 w-4 text-brocom-orange" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                  </svg>
                </div>
              </div>
              <div *ngIf="formGroup.get('postalCode')?.errors?.['required'] && formGroup.get('postalCode')?.touched"
                class="text-red-500 text-xs mt-1">
                {{ 'Kod pocztowy jest wymagany.' | translate }}
              </div>
              <div *ngIf="postalCodeInvalid" class="text-red-600 text-xs mt-1.5 flex items-start gap-1 font-medium">
                <svg class="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
                <span *ngIf="formGroup.get('otherCountry')?.value === 'TN'">{{ 'Wpisz prawidłowy, 4-cyfrowy kod pocztowy w Tunezji (np. 1000).' | translate }}</span>
                <span *ngIf="formGroup.get('otherCountry')?.value !== 'TN'">{{ 'Kod pocztowy jest nieprawidłowy dla wybranego kraju. Sprawdź go.' | translate }}</span>
              </div>
              <div *ngIf="postalValidationMessage" class="text-red-600 text-xs mt-1.5 font-medium" role="alert">
                {{ postalValidationMessage }}
                <button type="button" (click)="retryShippingRateLookup()"
                  class="ml-1 font-semibold underline underline-offset-2">{{ 'Spróbuj ponownie' | translate }}</button>
              </div>
            </div>

            <div>
              <label for="city" class="block text-xs sm:text-sm font-bold text-gray-900 mb-1 sm:mb-2">
                {{ 'Miasto' | translate }} <span class="text-orange-500">*</span>
              </label>
              <input id="city" formControlName="city" type="text"
                class="w-full px-3 sm:px-4 py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange focus:border-transparent transition-all"
                [class.border-red-500]="formGroup.get('city')?.invalid && formGroup.get('city')?.touched"
                [placeholder]="'Wpisz nazwę miasta' | translate" required />
              <div *ngIf="formGroup.get('city')?.errors?.['required'] && formGroup.get('city')?.touched"
                class="text-red-500 text-xs mt-1">
                {{ 'Miasto jest wymagane.' | translate }}
              </div>
              <div *ngIf="formGroup.get('city')?.errors?.['minlength'] && formGroup.get('city')?.touched"
                class="text-red-500 text-xs mt-1">
                {{ 'Nazwa miasta musi zawierać co najmniej 2 znaki.' | translate }}
              </div>
            </div>

            <div class="relative country-dropdown-container">
              <label class="block text-xs sm:text-sm font-bold text-gray-900 mb-1 sm:mb-2">
                {{ 'Kraj' | translate }} <span class="text-orange-500">*</span>
              </label>

              <!-- Hidden input bound to formControlName so form validation and value tracking work seamlessly -->
              <input type="hidden" formControlName="otherCountry" />

              <!-- Dropdown Trigger Button -->
              <button
                type="button"
                id="otherCountry"
                (click)="toggleCountryDropdown($event)"
                class="w-full px-3 sm:px-4 py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange focus:border-transparent transition-all bg-white flex items-center justify-between cursor-pointer text-left shadow-sm"
                [class.ring-2]="isCountryDropdownOpen"
                [class.ring-brocom-orange]="isCountryDropdownOpen"
                [class.border-transparent]="isCountryDropdownOpen"
                [class.border-red-500]="formGroup.get('otherCountry')?.invalid && formGroup.get('otherCountry')?.touched">
                <span *ngIf="displayCountry" class="font-medium text-gray-900 truncate">
                  {{ displayCountry }}
                </span>
                <span *ngIf="!displayCountry" class="text-gray-400">
                  {{ 'Wybierz kraj' | translate }}
                </span>
                <svg class="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 transition-transform duration-200 shrink-0 ml-2"
                  [class.rotate-180]="isCountryDropdownOpen"
                  [class.text-brocom-orange]="isCountryDropdownOpen"
                  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <div *ngIf="formGroup.get('otherCountry')?.invalid && formGroup.get('otherCountry')?.touched"
                class="text-red-500 text-xs mt-1">
                {{ 'Kraj jest wymagany.' | translate }}
              </div>

              <!-- Searchable Dropdown Menu -->
              <div
                *ngIf="isCountryDropdownOpen"
                class="absolute z-50 left-0 right-0 mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden animate-fadeIn"
                (click)="$event.stopPropagation()">
                
                <!-- Sticky Search Header -->
                <div class="p-2 border-b border-gray-100 bg-gray-50/80">
                  <div class="relative">
                    <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      #countrySearchInput
                      type="text"
                      [value]="countrySearchText"
                      (input)="onCountrySearch($any($event.target).value)"
                      [placeholder]="'Szukaj kraju...' | translate"
                      class="w-full pl-9 pr-8 py-2 text-xs sm:text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange focus:border-transparent text-gray-900 placeholder-gray-400"
                    />
                    <button
                      *ngIf="countrySearchText"
                      type="button"
                      (click)="clearCountrySearch($event)"
                      class="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5"
                      [title]="'Wyczyść wyszukiwanie' | translate">
                      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                <!-- Scrollable Country List -->
                <div class="max-h-56 sm:max-h-60 overflow-y-auto divide-y divide-gray-50 py-1">
                  <button
                    type="button"
                    *ngFor="let country of filteredCountries"
                    (click)="selectCountry(country, $event)"
                    class="w-full text-left px-3.5 py-2.5 sm:py-3 text-xs sm:text-sm flex items-center justify-between transition-colors hover:bg-orange-50 hover:text-brocom-orange group cursor-pointer"
                    [class.bg-orange-50]="formGroup.get('otherCountry')?.value === country.code"
                    [class.text-brocom-orange]="formGroup.get('otherCountry')?.value === country.code"
                    [class.font-semibold]="formGroup.get('otherCountry')?.value === country.code"
                    [class.text-gray-700]="formGroup.get('otherCountry')?.value !== country.code">
                    <span class="truncate">{{ country.name }}</span>
                    <div class="flex items-center gap-1.5 shrink-0 ml-2">
                      <span class="text-[11px] font-mono text-gray-400 group-hover:text-orange-500 uppercase">{{ country.code }}</span>
                      <svg *ngIf="formGroup.get('otherCountry')?.value === country.code"
                        class="w-4 h-4 text-brocom-orange" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                      </svg>
                    </div>
                  </button>

                  <!-- Empty state when no matches -->
                  <div *ngIf="filteredCountries.length === 0" class="py-6 px-4 text-center text-xs text-gray-400">
                    {{ 'Nie znaleziono krajów pasujących do hasła „' | translate }}<span class="font-medium text-gray-600">{{ countrySearchText }}</span>”.
                  </div>
                </div>
              </div>
            </div>

            <div class="sm:col-span-2">
              <label for="otherAddressInfo" class="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
                {{ 'Dodatkowe informacje o adresie (opcjonalnie)' | translate }}
              </label>
              <textarea id="otherAddressInfo" formControlName="otherAddressInfo" rows="2"
                class="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange focus:border-transparent transition-all resize-y"
                [placeholder]="'Dodatkowe informacje o adresie, punktach orientacyjnych lub szczególne instrukcje dotyczące dostawy…' | translate"></textarea>
            </div>

            <!-- Shipping Status & Rates Section -->
            <div *ngIf="shippingState$ | async as shippingState" class="sm:col-span-2 mt-1 pt-3 border-t border-gray-100">
              <div *ngIf="!shippingState.loading && !shippingState.error && !shippingState.options.length && !isDeliveryAddressComplete"
                class="rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-xs sm:text-sm text-amber-800"
                role="status">
                {{ 'Uzupełnij wymagane pola: kraj, ulica, kod pocztowy i miasto. Sprawdzimy dostępność dostawy oraz jej koszt.' | translate }}
              </div>

              <!-- Loading indicator -->
              <div *ngIf="shippingState.loading" class="flex items-center gap-3 p-3 bg-blue-50/70 border border-blue-200/80 rounded-xl">
                <svg class="animate-spin h-4 w-4 text-blue-600 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                </svg>
                <span class="text-xs sm:text-sm text-blue-700 font-medium">{{ 'Obliczanie aktualnych stawek FedEx dla podanego adresu…' | translate }}</span>
              </div>

              <!-- Undeliverable address error -->
              <div *ngIf="shippingState.error && !shippingState.loading"
                class="flex items-start gap-3 p-3.5 sm:p-4 bg-red-50 border border-red-200 rounded-xl text-red-800">
                <svg class="w-5 h-5 text-red-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                <div>
                  <p class="text-xs sm:text-sm font-semibold">
                    {{ 'Nie udało się połączyć z przewoźnikiem. Skontaktuj się z obsługą klienta, aby dokończyć zamówienie.' | translate }}
                  </p>
                  <a href="mailto:sklep@brocomindustry.com?subject=Shipping%20rate%20assistance"
                    class="mt-1 inline-flex text-xs font-semibold text-red-700 underline underline-offset-2">
                    {{ 'Skontaktuj się z obsługą klienta' | translate }}
                  </a>
                  <button type="button" (click)="retryShippingRateLookup()"
                    class="mt-2 text-xs font-semibold text-red-700 underline underline-offset-2">
                    {{ 'Ponów sprawdzanie stawek za dostawę' | translate }}
                  </button>
                </div>
              </div>

              <!-- Rates available -->
              <div *ngIf="shippingState.options.length > 0 && !shippingState.loading" class="space-y-3">
                <div class="flex items-center justify-between">
                  <div class="flex items-center gap-2">
                    <span class="text-xs sm:text-sm font-bold text-gray-900">{{ 'Dostępne opcje dostawy' | translate }}</span>
                    <span class="inline-flex items-center gap-1 bg-purple-100 text-purple-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-purple-200">
                      {{ 'Zweryfikowane przez FedEx' | translate }}
                    </span>
                  </div>
                  <span class="text-xs text-gray-500">{{ 'Dostawa na podany adres jest dostępna' | translate }}</span>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3" role="radiogroup" [attr.aria-label]="'Usługa dostawy' | translate">
                  <label *ngFor="let option of shippingState.options"
                    class="flex items-center justify-between p-3 rounded-xl border cursor-pointer transition-all text-xs sm:text-sm"
                    [class.border-purple-500]="shippingState.selectedOption?.serviceType === option.serviceType"
                    [class.bg-purple-50]="shippingState.selectedOption?.serviceType === option.serviceType"
                    [class.ring-1]="shippingState.selectedOption?.serviceType === option.serviceType"
                    [class.ring-purple-400]="shippingState.selectedOption?.serviceType === option.serviceType"
                    [class.border-gray-200]="shippingState.selectedOption?.serviceType !== option.serviceType"
                    [class.bg-white]="shippingState.selectedOption?.serviceType !== option.serviceType">
                    <div class="flex items-start gap-2.5 min-w-0 pr-2">
                      <input type="radio" name="billingShippingOption"
                        [checked]="shippingState.selectedOption?.serviceType === option.serviceType"
                        [attr.aria-label]="option.serviceName + ', ' + formatShippingPrice(option)"
                        (change)="selectShippingOption(option)"
                        class="mt-0.5 h-4 w-4 flex-shrink-0 accent-purple-600 focus:ring-purple-500" />
                      <div class="min-w-0 flex-1">
                        <div class="font-medium text-gray-800 whitespace-nowrap overflow-hidden text-ellipsis"
                          [title]="option.serviceName">{{ option.serviceName }}</div>
                        <div class="flex items-center gap-1.5 mt-1">
                          <span class="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md"
                            [class.bg-emerald-50]="getDeliveryBadge(option).isFast"
                            [class.text-emerald-700]="getDeliveryBadge(option).isFast"
                            [class.border]="getDeliveryBadge(option).isFast"
                            [class.border-emerald-200]="getDeliveryBadge(option).isFast"
                            [class.bg-gray-100]="!getDeliveryBadge(option).isFast"
                            [class.text-gray-600]="!getDeliveryBadge(option).isFast">
                            <svg class="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                            </svg>
                            {{ getDeliveryBadge(option).text }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <span class="font-bold text-gray-900 shrink-0 whitespace-nowrap">
                      {{ formatShippingPrice(option) }}
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-gray-200">
          <label class="flex items-start gap-3 text-sm font-semibold text-gray-900">
            <input type="checkbox" formControlName="deliverySameAsBilling"
              class="mt-0.5 rounded border-gray-300 text-brocom-orange focus:ring-brocom-orange" />
            <span>{{ 'Dostawa na adres rozliczeniowy' | translate }}</span>
          </label>

          <div *ngIf="formGroup.get('deliverySameAsBilling')?.value === false"
            class="mt-4 border-t border-gray-100 pt-4">
            <h3 class="text-base sm:text-lg font-bold text-gray-900">{{ 'Adres dostawy' | translate }}</h3>
            <p class="text-xs text-gray-500 mt-1 mb-4">{{ 'Koszt dostawy zostanie ponownie obliczony dla tego adresu.' | translate }}</p>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
              <div>
                <label for="deliveryRecipientName" class="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">{{ 'Odbiorca' | translate }}</label>
                <input id="deliveryRecipientName" formControlName="deliveryRecipientName" type="text"
                  class="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange"
                  [placeholder]="'Imię i nazwisko odbiorcy' | translate" />
              </div>
              <div>
                <label for="deliveryCompanyName" class="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">{{ 'Firma (opcjonalnie)' | translate }}</label>
                <input id="deliveryCompanyName" formControlName="deliveryCompanyName" type="text"
                  class="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange"
                  [placeholder]="'Nazwa firmy' | translate" />
              </div>
              <div>
                <label for="deliveryAddress" class="block text-xs sm:text-sm font-bold text-gray-900 mb-2">{{ 'Ulica i numer' | translate }} <span class="text-orange-500">*</span></label>
                <input id="deliveryAddress" formControlName="deliveryAddress" type="text"
                  class="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange"
                  [placeholder]="'Ulica i numer' | translate" />
                <p *ngIf="formGroup.get('deliveryAddress')?.invalid && formGroup.get('deliveryAddress')?.touched" class="text-red-500 text-xs mt-1">{{ 'Ulica i numer są wymagane.' | translate }}</p>
              </div>
              <div>
                <label for="deliveryCountry" class="block text-xs sm:text-sm font-bold text-gray-900 mb-2">{{ 'Kraj' | translate }} <span class="text-orange-500">*</span></label>
                <select id="deliveryCountry" formControlName="deliveryCountry"
                  class="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange">
                  <option value="">{{ 'Wybierz kraj' | translate }}</option>
                  <option *ngFor="let country of countries" [value]="country.code">{{ country.name }}</option>
                </select>
                <p *ngIf="formGroup.get('deliveryCountry')?.invalid && formGroup.get('deliveryCountry')?.touched" class="text-red-500 text-xs mt-1">{{ 'Kraj jest wymagany.' | translate }}</p>
              </div>
              <div>
                <label for="deliveryPostalCode" class="block text-xs sm:text-sm font-bold text-gray-900 mb-2">{{ 'Kod pocztowy' | translate }} <span class="text-orange-500">*</span></label>
                <input id="deliveryPostalCode" formControlName="deliveryPostalCode" type="text"
                  class="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange"
                  [placeholder]="'Kod pocztowy' | translate" />
                <p *ngIf="formGroup.get('deliveryPostalCode')?.invalid && formGroup.get('deliveryPostalCode')?.touched" class="text-red-500 text-xs mt-1">{{ 'Wpisz prawidłowy kod pocztowy.' | translate }}</p>
              </div>
              <div>
                <label for="deliveryCity" class="block text-xs sm:text-sm font-bold text-gray-900 mb-2">{{ 'Miasto' | translate }} <span class="text-orange-500">*</span></label>
                <input id="deliveryCity" formControlName="deliveryCity" type="text"
                  class="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange"
                  [placeholder]="'Miasto' | translate" />
                <p *ngIf="formGroup.get('deliveryCity')?.invalid && formGroup.get('deliveryCity')?.touched" class="text-red-500 text-xs mt-1">{{ 'Miasto jest wymagane.' | translate }}</p>
              </div>
              <div>
                <label for="deliveryStateProvince" class="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">{{ 'Województwo / region (opcjonalnie)' | translate }}</label>
                <input id="deliveryStateProvince" formControlName="deliveryStateProvince" type="text"
                  class="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange"
                  [placeholder]="'Województwo lub region' | translate" />
              </div>
              <div>
                <label for="deliveryPhone" class="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">{{ 'Telefon (opcjonalnie)' | translate }}</label>
                <input id="deliveryPhone" formControlName="deliveryPhone" type="tel"
                  class="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange"
                  [placeholder]="'Numer telefonu do kontaktu w sprawie dostawy' | translate" />
              </div>
            </div>
          </div>
        </div>

        <!-- Contact Information -->
        <div class="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-gray-200">
          <div class="pb-2.5 mb-3 border-b border-gray-100">
            <h3 class="text-base sm:text-lg font-bold text-gray-900">
              {{ 'Dane kontaktowe' | translate }}
            </h3>
            <p class="text-xs text-gray-500 mt-0.5">{{ 'Podaj dane osoby kontaktowej, aby otrzymywać informacje o zamówieniu.' | translate }}</p>
          </div>

          <!-- Contact Information Validation Message -->
          <div *ngIf="hasContactInformationErrors()"
            class="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div class="flex items-start">
              <svg class="w-4 h-4 sm:w-5 sm:h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" fill="none"
                stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div>
                <p class="text-xs sm:text-sm font-semibold text-red-800 mb-1">{{ 'Uzupełnij wymagane dane kontaktowe.' | translate }}</p>
                <p class="text-xs sm:text-sm text-red-700">{{ 'Brakujące pola:' | translate }} {{ getMissingContactFields().join(',
                  ') }}</p>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
            <div>
              <label for="firstName" class="block text-xs sm:text-sm font-bold text-gray-900 mb-1 sm:mb-2">
                {{ 'Imię' | translate }} <span class="text-orange-500">*</span>
              </label>
              <input id="firstName" formControlName="firstName" type="text"
                class="w-full px-3 sm:px-4 py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange focus:border-transparent transition-all"
                [placeholder]="'Wpisz imię' | translate" required />
              <div *ngIf="formGroup.get('firstName')?.invalid && formGroup.get('firstName')?.touched"
                class="text-red-500 text-xs mt-1">
                {{ 'Imię jest wymagane.' | translate }}
              </div>
            </div>

            <div>
              <label for="lastName" class="block text-xs sm:text-sm font-bold text-gray-900 mb-1 sm:mb-2">
                {{ 'Nazwisko' | translate }} <span class="text-orange-500">*</span>
              </label>
              <input id="lastName" formControlName="lastName" type="text"
                class="w-full px-3 sm:px-4 py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange focus:border-transparent transition-all"
                [placeholder]="'Wpisz nazwisko' | translate" required />
              <div *ngIf="formGroup.get('lastName')?.invalid && formGroup.get('lastName')?.touched"
                class="text-red-500 text-xs mt-1">
                {{ 'Last name is required' | translate }}
              </div>
            </div>

            <div>
              <label for="phoneNumber" class="block text-xs sm:text-sm font-bold text-gray-900 mb-1 sm:mb-2">
                {{ 'Phone No. (International)' | translate }} <span class="text-orange-500">*</span>
              </label>
              <div class="relative flex rounded-lg shadow-xs">
                <div class="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-700 text-xs sm:text-sm font-semibold select-none">
                  <svg class="w-3.5 h-3.5 mr-1 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                  </svg>
                  <span>{{ currentDialCode }}</span>
                </div>
                <input id="phoneNumber" formControlName="phoneNumber" type="tel"
                  (blur)="onPhoneBlur()"
                  class="w-full px-3 sm:px-4 py-2.5 text-sm sm:text-base border border-gray-300 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange focus:border-transparent transition-all"
                  [class.border-red-500]="formGroup.get('phoneNumber')?.invalid && formGroup.get('phoneNumber')?.touched"
                  [placeholder]="phonePlaceholder" required />
              </div>
              <div *ngIf="formGroup.get('phoneNumber')?.invalid && formGroup.get('phoneNumber')?.touched"
                class="text-red-500 text-xs mt-1">
                <span *ngIf="formGroup.get('phoneNumber')?.errors?.['required']">{{ 'Numer telefonu jest wymagany.' | translate }}</span>
                <span *ngIf="formGroup.get('phoneNumber')?.errors?.['invalidInternationalPhone']">
                  {{ 'Wpisz prawidłowy międzynarodowy numer telefonu (np.' | translate }} {{ currentDialCode }} {{ phonePlaceholder }}).
                </span>
              </div>
              <p class="text-[11px] text-gray-400 mt-1">{{ 'Numer jest wymagany do otrzymywania powiadomień od kuriera.' | translate }}</p>
            </div>

            <div>
              <label for="email" class="block text-xs sm:text-sm font-bold text-gray-900 mb-1 sm:mb-2">
                {{ 'Adres e-mail' | translate }} <span class="text-orange-500">*</span>
              </label>
              <input id="email" formControlName="email" type="email"
                class="w-full px-3 sm:px-4 py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange focus:border-transparent transition-all"
                [placeholder]="'example@company.com' | translate" required />
              <div *ngIf="formGroup.get('email')?.invalid && formGroup.get('email')?.touched"
                class="text-red-500 text-xs mt-1">
                <span *ngIf="formGroup.get('email')?.errors?.['required']">{{ 'Adres e-mail jest wymagany.' | translate }}</span>
                <span *ngIf="formGroup.get('email')?.errors?.['email']">{{ 'Wpisz prawidłowy adres e-mail.' | translate }}</span>
              </div>
            </div>
          </div>
        </div>

      <!-- Other Details / Order Notes -->
      <div class="bg-white rounded-xl p-4 sm:p-5 shadow-sm border border-gray-200">
        <div class="pb-2.5 mb-3 border-b border-gray-100">
          <h3 class="text-base sm:text-lg font-bold text-gray-900">
            {{ isCompanyUser ? 'Informacje dodatkowe' : 'Uwagi do zamówienia' }}
          </h3>
          <p class="text-xs text-gray-500 mt-0.5">
            {{ isCompanyUser ? 'Opcjonalne informacje wewnętrzne i uwagi do zamówienia' : 'Opcjonalne uwagi lub instrukcje dotyczące zamówienia' }}
          </p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
          <div *ngIf="isCompanyUser">
            <label for="orderingParty" class="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
              {{ 'Osoba składająca zamówienie' | translate }}
            </label>
            <input id="orderingParty" formControlName="orderingParty" type="text"
              class="w-full px-3 sm:px-4 py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange focus:border-transparent transition-all"
              [placeholder]="'Imię i nazwisko osoby składającej zamówienie' | translate" />
          </div>

          <div [class.sm:col-span-2]="!isCompanyUser">
            <label for="noteBrocom" class="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
              {{ 'Uwagi dla BROCOM' | translate }}
            </label>
            <input id="noteBrocom" formControlName="noteBrocom" type="text"
              class="w-full px-3 sm:px-4 py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange focus:border-transparent transition-all"
              [placeholder]="'Dodatkowe uwagi lub instrukcje dla BROCOM' | translate" />
          </div>

          <div *ngIf="isCompanyUser" class="sm:col-span-2">
            <label for="departmentOrder" class="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
              {{ 'Dział / numer zamówienia' | translate }}
            </label>
            <input id="departmentOrder" formControlName="departmentOrder" type="text" maxlength="30"
              class="w-full px-3 sm:px-4 py-2.5 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brocom-orange focus:border-transparent transition-all"
              [placeholder]="'Nazwa działu i wewnętrzny numer zamówienia (maks. 30 znaków)' | translate" />
            <p class="text-xs text-gray-500 mt-1 sm:mt-2">{{ 'Wpisz nazwę działu oraz wewnętrzny numer zamówienia (maksymalnie 30 znaków łącznie).' | translate }}</p>
          </div>
        </div>
      </div>
    </form>
  </div>
</div>